import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import VoteButtons from "../components/VoteButtons";
import CategoryBadge from "../components/CategoryBadge";
import { ArrowLeft, MapPin, Building2, Calendar } from "lucide-react";

export default function ProjectDetail({ onOpenAuth }) {
  const { categorySlug, slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/by-slug/${categorySlug}/${slug}`)
      .then(({ data }) => {
        setProject(data);
        if (data?.canonical_path && data.canonical_path !== location.pathname) {
          navigate(data.canonical_path, { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [categorySlug, location.pathname, navigate, slug]);

  useEffect(() => {
    if (project?.title) {
      document.title = `${project.title} | fără hârtie.ro`;
    }
  }, [project?.title]);

  if (loading) return <div className="container-civic py-16 text-foreground/60">Se încarcă…</div>;
  if (!project) return (
    <div className="container-civic py-16">
      <p>Proiectul nu a fost găsit.</p>
      <Link to="/proiecte" className="underline">Înapoi la proiecte</Link>
    </div>
  );

  return (
    <main className="container-civic py-12 md:py-16 max-w-4xl">
      <Link to="/proiecte" className="text-sm inline-flex items-center gap-1.5 text-foreground/60 hover:text-primary mb-6">
        <ArrowLeft size={14}/> Toate proiectele
      </Link>

      <div className="flex items-center gap-3 flex-wrap mb-4">
        <CategoryBadge category={project.category} />
        <span className="text-[11px] uppercase tracking-[0.18em] text-foreground/60 font-semibold">{project.status}</span>
      </div>

      <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.02em] leading-[1.02]" data-testid="project-title">
        {project.title}
      </h1>

      <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-foreground/70">
        <span className="inline-flex items-center gap-1.5"><MapPin size={14}/> {project.city}, {project.county}</span>
        <span className="inline-flex items-center gap-1.5"><Building2 size={14}/> {project.authority}</span>
        <span className="inline-flex items-center gap-1.5"><Calendar size={14}/> {new Date(project.created_at).toLocaleDateString("ro-RO")}</span>
      </div>

      <div className="card-flat p-5 mt-8 flex items-center justify-between gap-6 flex-wrap">
        <div>
          <div className="label-overline">Susțin / Nu susțin</div>
          <p className="font-display text-2xl font-bold mt-1 tabular-nums">
            {project.upvotes} pro · {project.downvotes} contra
          </p>
        </div>
        <VoteButtons project={project} onChange={setProject} requestLogin={() => onOpenAuth?.("login")} size="lg" />
      </div>

      <section className="mt-10 prose max-w-none">
        <h2 className="font-display text-2xl font-bold mb-3">Descriere</h2>
        <p className="text-foreground/80 leading-relaxed whitespace-pre-line">{project.description}</p>
      </section>
    </main>
  );
}
